import {Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';

import {BodyTypeViewModel} from '../../../viewModels/bodyType.viewModel';
import {UserProfileViewModel} from '../../../viewModels/userProfile.viewModel';
import {UsersService} from '../../../services/users.service';
import {NotificationsService} from '../../../services/notification.service';
import {LocalisationService} from '../../../services/localisation.service';
import {DailyMealViewModel} from '../../../viewModels/dailyMeal.viewModel';
import {forkJoin} from 'rxjs';
import {DietViewModel} from '../../../viewModels/diet.viewModel';
import {AllergyViewModel} from '../../../../meals/components/viewModels/allergy.viewModel';
import {ProductsService} from '../../../services/products.service';
import {TrainingTargetViewModel} from '../../../viewModels/trainingTarget.viewModel';
import {ProtocolViewModel} from '../../../viewModels/protocol.viewModel';

@Component({
    selector: 'app-body-section',
    templateUrl: './body-section.component.html',
    styleUrls: ['./body-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BodySectionComponent implements OnInit {
    @Input() editable = true;
    @Input() notifications = true;

    loading = true;
    dailyMeals: DailyMealViewModel[];
    diets: DietViewModel[];

    currentlySelectedDailyMeal: DailyMealViewModel;
    currentlySelectedDiet: DietViewModel;
    currentlySelectedTrainingTarget: TrainingTargetViewModel;

    allergies: AllergyViewModel[];
    enabledByAllergy: { [key: number]: boolean; } = {};
    trainingTargets: TrainingTargetViewModel[];

    // Protocol-related properties (protocol is read-only, set by admin)
    currentProtocol: ProtocolViewModel | null = null;
    isProtocolEnabled: boolean = false;
    isDuplicateDaysEnabled: boolean = false;
    isDinnerSameForLunchEnabled: boolean = false;
    maxProductPerRecipe: number = 5;
    useSimilarProductsBetweenRecipes: boolean = true;

    constructor(
        private readonly usersService: UsersService,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly productsService: ProductsService,
    ) {
    }

    ngOnInit() {
        forkJoin(
            this.usersService.getUserDailyMeals(),
            this.usersService.getUserDiets(),
            this.productsService.getAllergies(),
            this.usersService.getUserTrainingTargets(),
        ).subscribe(
            ([dailyMeals, diets, allergies, trainingTargets]) => {
                this.dailyMeals = dailyMeals;
                this.diets = diets;
                this.allergies = allergies;
                this.trainingTargets = trainingTargets;

                this.loading = false;

                this.changeDetectorRef.detectChanges();

                if (this.usersService.userProfile) {
                    this.initCurrentUserProfile(this.usersService.userProfile);
                }
            }
        );
    }

    updateProfile() {
        this.loading = true;

        let trainingTarget = null;

        if (this.currentlySelectedTrainingTarget) {
            trainingTarget = {id: this.currentlySelectedTrainingTarget.id};
        }

        // Base profile data
        const profileData: any = {
            daily_meals: {
                id: this.currentlySelectedDailyMeal.id
            },
            diet: this.currentlySelectedDiet ? {id: this.currentlySelectedDiet.id} : null,
            allergies: Object.keys(this.enabledByAllergy).filter(k => this.enabledByAllergy[k]).map(k => +k).map(id => {
                return {id};
            }),
        };

        // Only add protocol-related settings if protocol is enabled by admin
        if (this.isProtocolEnabled) {
            // Don't send protocol object - it's managed by admin, only send user settings
            profileData.is_duplicate_days_enabled = this.isDuplicateDaysEnabled;
            profileData.is_dinner_same_for_lunch_enabled = this.isDinnerSameForLunchEnabled;
            profileData.max_product_per_recipe = this.maxProductPerRecipe;
            profileData.use_similar_products_between_recipes = this.useSimilarProductsBetweenRecipes;
        }

        this.usersService
            .updateUserProfile(profileData)
            .subscribe(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();

                if (this.notifications) {
                    this.notificationService.success(this.localisationService.localise('settings_profile_updated_successfully'));
                }
            });
    }

    get saveButtonIsDisabled(): boolean {
        return !this.currentlySelectedDailyMeal;
    }

    // Getter to check if protocol section should be visible (only if protocol is enabled by admin)
    get showProtocolSection(): boolean {
        return this.isProtocolEnabled;
    }

    private initCurrentUserProfile(userProfile: UserProfileViewModel) {
        this.currentlySelectedDailyMeal = userProfile.daily_meals ? this.dailyMeals.find(dm => dm.id === userProfile.daily_meals.id) : undefined;
        this.currentlySelectedDiet = userProfile.diet ? this.diets.find(d => d.id === userProfile.diet.id) : undefined;
        this.currentlySelectedTrainingTarget = userProfile.training_target ? this.trainingTargets.find(tt => tt.id === userProfile.training_target.id) : undefined;

        // Initialize protocol-related fields (protocol is read-only, assigned by admin)
        this.isProtocolEnabled = userProfile.is_protocol_enabled || false;
        
        if (this.isProtocolEnabled && userProfile.protocol) {
            this.currentProtocol = userProfile.protocol;
            this.isDuplicateDaysEnabled = userProfile.is_duplicate_days_enabled || false;
            this.isDinnerSameForLunchEnabled = userProfile.is_dinner_same_for_lunch_enabled || false;
            this.maxProductPerRecipe = userProfile.max_product_per_recipe || 5;
            this.useSimilarProductsBetweenRecipes = userProfile.use_similar_products_between_recipes !== undefined ? userProfile.use_similar_products_between_recipes : true;
        } else {
            // Reset protocol settings if protocol is not enabled
            this.currentProtocol = null;
            this.isDuplicateDaysEnabled = false;
            this.isDinnerSameForLunchEnabled = false;
            this.maxProductPerRecipe = 5;
            this.useSimilarProductsBetweenRecipes = true;
        }

        this.initEnabledByAllergy(userProfile);

        this.changeDetectorRef.markForCheck();
    }


    private initEnabledByAllergy(userProfile: UserProfileViewModel) {
        this.allergies.forEach(a => this.enabledByAllergy[a.id] = false);

        if (userProfile.allergies) {
            userProfile.allergies.forEach(a => {
                this.enabledByAllergy[a.id] = true;
            });
        }
    }

}
